import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Search, RefreshCw, FileText,
  BookOpen, AlertTriangle, Rocket, X, Save,
  Users, ChevronDown,
  ArrowUp, ArrowDown, Check, Edit2, Trash2,
  CircleMinus, CircleCheck, Settings,
} from 'lucide-react';
import Toast, { ToastMessage } from '../common/Toast';
import Modal from '../common/Modal';
import Button from '../common/Button';
import FormControl from '../common/FormControl';
import Badge from '../common/Badge';
import Skeleton from '../common/Skeleton';
import NoDataFound from '../common/NoDataFound';
import StatusSwitch from '../common/StatusSwitch';
import Pagination from '../common/Pagination';
import apiClient from '../../services/apiService';
import applicableLawsService, { ApplicableLaw } from '../../services/applicableLawsService';
import { authorityApi as authorityApiService } from '../../services/authorityApi';
import { Authority, ApiResponse } from '../../types/authority';
import sopInstanceService, { LocationLite } from '../../services/sopInstanceService';
import { usePermissions, usePermission } from '../../contexts/PermissionContext';
import { confirmWarning, confirmActive } from '../common/confirm';
import { statusVariant } from '../../utils/statusVariant';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedInput, ValidatedTextarea, ValidatedDatePicker } from '../../validation/ValidatedFields';
import { litOrgService, type PickablePosition } from '../../services/litInstanceService';
import { UserCheck } from 'lucide-react';

const BASE = '/api/v1/com';

const OBLIGATION_TYPES = [
  'Filing of a Return',
  'Submission of Statutory Information',
  'Other Obligation',
] as const;

const CRITICALITIES = ['Low', 'Medium', 'High', 'Critical'] as const;
const ROLES = ['PERFORM', 'APPROVE', 'REVIEW', 'NOTIFY', 'OTHER'] as const;
const ROLE_LABEL: Record<string, string> = {
  PERFORM: 'Performs', APPROVE: 'Approves', REVIEW: 'Reviews',
  NOTIFY: 'Is Notified', OTHER: 'Other',
};

interface StepAuthority { id: number; authority_id: number; authority_name: string; role: string; }
interface ObStep { id: number; step_number: number; step_name: string; description?: string | null; authorities: StepAuthority[]; }
interface Obligation {
  id: number; code?: string | null; name: string; law_id?: number | null; law_name?: string | null;
  form_reference?: string | null;
  description?: string | null; obligation_type?: string | null;
  provision_description?: string | null; penal_consequences?: string | null;
  is_recurring?: boolean;
  ob_status?: string | null; step_count?: number;
  criticality?: string | null;
  is_active?: number;
}

// ── Card skeleton ─────────────────────────────────────────────────────────────
const CardSkeleton: React.FC<{ count?: number }> = ({ count = 6 }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="action-card action-card--skeleton">
        <div className="action-card__skel-header">
          <Skeleton.Box width={34} height={34} radius={8} />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Skeleton.Box width="60%" height={12} />
            <Skeleton.Box width="40%" height={10} />
          </div>
          <Skeleton.Pill width={64} height={20} />
        </div>
        <div className="action-card__skel-meta">
          <Skeleton.Box width={80} height={10} />
          <Skeleton.Box width={100} height={10} />
          <Skeleton.Box width={70} height={10} />
        </div>
      </div>
    ))}
  </>
);

// Shows the current occupant of the picked position (mirrors the litigation
// position picker's "Assigned to" line).
const occupantOf = (p: PickablePosition | null): string | null =>
  (p?.occupant_name || '').trim() || null;

const AssigneeHint: React.FC<{ pos: PickablePosition | null }> = ({ pos }) => {
  if (!pos) return null;
  return (
    <div className="flex items-start gap-2 px-3 py-2 rounded-lg border text-xs bg-emerald-50 border-emerald-200">
      <UserCheck size={13} className="text-emerald-600 mt-0.5 shrink-0" />
      <span className="text-emerald-800">
        <span className="text-gray-500">Assigned to: </span>
        {occupantOf(pos)
          ? <span className="font-medium">{occupantOf(pos)}</span>
          : <span className="italic text-gray-400">Position vacant</span>}
      </span>
    </div>
  );
};

// ── Invoke modal ──────────────────────────────────────────────────────────────
const InvokeModal: React.FC<{
  obligation: Obligation; locations: LocationLite[]; authorities: Authority[];
  onClose: () => void; onSuccess: (msg: string) => void;
}> = ({ obligation, locations, authorities, onClose, onSuccess }) => {
  const [periodLabel, setPeriodLabel] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [locationId, setLocationId] = useState<number | null>(null);
  // Invoke-time maker/checker override (UAT #9). These pick the exact POSITION
  // that performs / approves this invocation — an authority can own several
  // positions, so authority routing would fan out to multiple occupants. The
  // task routes to the picked seat's occupant (Work > Compliance).
  const [makerPosId, setMakerPosId] = useState<number | null>(null);
  const [checkerPosId, setCheckerPosId] = useState<number | null>(null);
  const [makerErr, setMakerErr] = useState<string>('');
  const [checkerErr, setCheckerErr] = useState<string>('');
  const [locErr, setLocErr] = useState<string>('');
  const [positions, setPositions] = useState<PickablePosition[]>([]);
  const [saving, setSaving] = useState(false);
  const vh = useFieldValidation();

  useEffect(() => {
    litOrgService.getPickablePositions().then(setPositions).catch(() => {});
  }, []);

  const makerPos = positions.find(p => p.id === makerPosId) ?? null;
  const checkerPos = positions.find(p => p.id === checkerPosId) ?? null;
  const positionOpts = positions.map(p => ({
    value: String(p.id),
    label: `${p.name ?? `Position #${p.id}`}${p.location_name ? ` (${p.location_name})` : ''}`,
  }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const fieldsOk = vh.validateAll([
      { name: 'period_label', code: 109, value: periodLabel },
      { name: 'due_date', code: 305, value: dueDate },
    ]);
    // Location, Maker + Checker are all required (no silent template default).
    const lErr = locationId == null ? 'Location is required' : '';
    const mErr = makerPosId == null ? 'Maker is required' : '';
    let cErr = checkerPosId == null ? 'Checker is required' : '';
    // Maker-checker separation: different seat AND different current occupant.
    if (!cErr && makerPosId != null) {
      if (makerPosId === checkerPosId) {
        cErr = 'Maker and Checker must be different positions';
      } else if (makerPos?.occupant_user_id != null
                 && makerPos.occupant_user_id === checkerPos?.occupant_user_id) {
        cErr = 'Maker and Checker are the same person';
      }
    }
    setLocErr(lErr); setMakerErr(mErr); setCheckerErr(cErr);
    if (!fieldsOk || lErr || mErr || cErr) return;
    setSaving(true);
    try {
      await apiClient.post(`${BASE}/obligations/${obligation.id}/invoke`, {
        period_label: periodLabel.trim() || null,
        due_date: dueDate || null,
        location_id: locationId,
        maker_position_id: makerPosId,
        checker_position_id: checkerPosId,
      });
      onSuccess(`Obligation "${obligation.name}" invoked.`);
    } catch (err: any) {
      onSuccess('error:' + (err?.response?.data?.detail || 'Failed to invoke'));
    } finally { setSaving(false); }
  };

  return (
    <Modal isOpen onClose={onClose} size="md">
      <Modal.Header>
        Invoke <span className="item-highlight">"{obligation.name}"</span>
      </Modal.Header>
      <Modal.Body>
        <form id="invoke-form" onSubmit={handleSubmit} className="grid grid-cols-2 gap-x-6 gap-y-4">
          <ValidatedInput
            vh={vh}
            name="period_label"
            code={109}
            label="Period Label"
            placeholder="e.g. Q1 FY2026, April 2026"
            value={periodLabel}
            onChange={e => setPeriodLabel(e.target.value)}
          />
          <ValidatedDatePicker
            vh={vh}
            name="due_date"
            code={305}
            label="Due Date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />
          <FormControl.SearchSelect
            label="Location"
            required
            options={locations.map(l => ({ value: String(l.id), label: l.name }))}
            value={locationId != null ? String(locationId) : ''}
            onChange={v => { setLocationId(v ? Number(v) : null); setLocErr(''); }}
            placeholder="Select location…"
            error={locErr}
          />
          <div className="flex flex-col gap-y-2">
            <FormControl.SearchSelect
              label="Maker (executor)"
              required
              options={positionOpts}
              value={makerPosId != null ? String(makerPosId) : ''}
              onChange={v => { setMakerPosId(v ? Number(v) : null); setMakerErr(''); }}
              placeholder="Select maker position…"
              error={makerErr}
              data-testid="SEL_invoke_maker_position"
            />
            <AssigneeHint pos={makerPos} />
          </div>
          <div className="flex flex-col gap-y-2">
            <FormControl.SearchSelect
              label="Checker (approver)"
              required
              options={positionOpts}
              value={checkerPosId != null ? String(checkerPosId) : ''}
              onChange={v => { setCheckerPosId(v ? Number(v) : null); setCheckerErr(''); }}
              placeholder="Select checker position…"
              error={checkerErr}
              data-testid="SEL_invoke_checker_position"
            />
            <AssigneeHint pos={checkerPos} />
          </div>
          {(obligation.step_count ?? 0) === 0 && (
            <div className="col-span-2 flex items-center gap-2 text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
              <AlertTriangle size={12} />
              No steps defined. Add steps before invoking.
            </div>
          )}
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="submit"
          form="invoke-form"
          variant="fill"
          icon={Rocket}
          disabled={saving || (obligation.step_count ?? 0) === 0}
        >
          Invoke
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ── Obligation form modal ─────────────────────────────────────────────────────
const ObligationModal: React.FC<{
  initial?: Obligation | null; laws: ApplicableLaw[];
  onClose: () => void; onSaved: (ob: Obligation) => void;
  onToggleActive?: (ob: Obligation, next: number) => Promise<void>;
}> = ({ initial, laws, onClose, onSaved, onToggleActive }) => {
  const [form, setForm] = useState({
    name: initial?.name ?? '',
    obligation_type: initial?.obligation_type ?? 'Filing of a Return',
    law_id: initial?.law_id ? String(initial.law_id) : '',
    form_reference: initial?.form_reference ?? '',
    provision_description: initial?.provision_description ?? '',
    penal_consequences: initial?.penal_consequences ?? '',
    is_recurring: initial?.is_recurring ?? true,
    ob_status: initial?.ob_status ?? 'DRAFT',
    description: initial?.description ?? '',
    criticality: initial?.criticality ?? 'Medium',
  });
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [isEditing, setIsEditing] = useState(!initial);
  // Guards against a double-click on "Edit" whose 2nd click lands on the
  // "Update" button that swaps into the same footer spot on the 1st click.
  const [editJustEnabled, setEditJustEnabled] = useState(false);
  const editGuardTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const vh = useFieldValidation();
  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    vh.reset();
    setIsEditing(!initial);
    setEditJustEnabled(false);
    if (editGuardTimer.current) clearTimeout(editGuardTimer.current);
    setForm({
      name: initial?.name ?? '',
      obligation_type: initial?.obligation_type ?? 'Filing of a Return',
      law_id: initial?.law_id ? String(initial.law_id) : '',
      form_reference: initial?.form_reference ?? '',
      provision_description: initial?.provision_description ?? '',
      penal_consequences: initial?.penal_consequences ?? '',
      is_recurring: initial?.is_recurring ?? true,
      ob_status: initial?.ob_status ?? 'DRAFT',
      description: initial?.description ?? '',
      criticality: initial?.criticality ?? 'Medium',
    });
    setSaving(false);
    setToggling(false);
  }, [initial]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    if (!vh.validateAll([
      { name: 'name', code: 109, value: form.name, required: true },
      { name: 'form_reference', code: 109, value: form.form_reference || '' },
      { name: 'provision_description', code: 110, value: form.provision_description || '' },
      { name: 'penal_consequences', code: 110, value: form.penal_consequences || '' },
    ])) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        law_id: form.law_id ? Number(form.law_id) : null,
      };
      const res = initial
        ? await apiClient.put(`${BASE}/obligations/${initial.id}`, payload)
        : await apiClient.post(`${BASE}/obligations`, payload);
      onSaved(res.data.data);
    } catch (err: any) { console.error(err); }
    finally { setSaving(false); }
  };

  const handleToggle = async () => {
    if (!initial || !onToggleActive) return;
    const next = (initial.is_active ?? 1) ? 0 : 1;
    const isCurrentlyActive = (initial.is_active ?? 1) === 1;
    const confirmFn = isCurrentlyActive ? confirmWarning : confirmActive;
    const ok = await confirmFn({
      title: next ? 'Reactivate this obligation?' : 'Inactivate this obligation?',
      text: next ? 'It will appear in active lists again.' : 'It will be hidden from active lists.',
      confirmText: next ? 'Yes, Reactivate' : 'Yes, Inactivate',
    });
    if (!ok) return;
    setToggling(true);
    try { await onToggleActive(initial, next); }
    finally { setToggling(false); }
  };

  const isActive = (initial?.is_active ?? 1) === 1;
  const isInvalid = !form.name.trim();
  const fieldsDisabled = !isEditing && !!initial;

  const lawOpts = laws.map(l => ({ value: String(l.id), label: l.law_name }));

  return (
    <Modal isOpen onClose={onClose} size="lg">
      <Modal.Header>
        {initial
          ? isEditing
            ? <>Edit <span className="item-highlight">"{initial.name}"</span></>
            : <span className="item-highlight">{initial.name}</span>
          : 'Create Statutory Obligation'}
      </Modal.Header>
      <Modal.Body>
        <form id="ob-form" onSubmit={handleSubmit} className="grid grid-cols-2 gap-x-6 gap-y-4">
          <div className="col-span-2">
            <ValidatedInput vh={vh} name="name" code={109} required label="Obligation Name" value={form.name}
              onChange={e => set('name', e.target.value)} placeholder="Enter obligation name" disabled={fieldsDisabled} />
          </div>

          <FormControl.RadioGroup
            label="Obligation Type"
            required
            name="ob_type"
            options={OBLIGATION_TYPES.map(t => ({ value: t, label: t }))}
            value={form.obligation_type}
            onChange={v => set('obligation_type', v)}
            disabled={fieldsDisabled}
          />

          <div className="flex flex-col gap-y-4">
            <FormControl.SearchSelect label="Act / Law" options={lawOpts} value={form.law_id}
              onChange={v => set('law_id', v ? String(v) : '')} placeholder="Select act…" clearable disabled={fieldsDisabled} />
            <ValidatedInput vh={vh} name="form_reference" code={109} label="Form / Return Reference" value={form.form_reference}
              onChange={e => set('form_reference', e.target.value)} placeholder="e.g. Form 26Q, ITR-6" disabled={fieldsDisabled} />
          </div>

          <ValidatedTextarea
            vh={vh}
            name="provision_description"
            code={110}
            label="Provision Description"
            rows={3}
            placeholder="Brief description of the legal provision…"
            value={form.provision_description}
            onChange={e => set('provision_description', e.target.value)}
            disabled={fieldsDisabled}
          />

          <ValidatedTextarea
            vh={vh}
            name="penal_consequences"
            code={110}
            label="Penal Consequences"
            rows={3}
            placeholder="Penalty / fine / imprisonment on non-compliance…"
            value={form.penal_consequences}
            onChange={e => set('penal_consequences', e.target.value)}
            disabled={fieldsDisabled}
          />

          <FormControl.SearchSelect label="Criticality"
            options={CRITICALITIES.map(c => ({ value: c, label: c }))}
            value={form.criticality} onChange={v => set('criticality', String(v ?? 'Medium'))} disabled={fieldsDisabled} />

        </form>
      </Modal.Body>
      <Modal.Footer className={initial ? 'app-modal-footer--split' : ''}>
        {initial && onToggleActive && (
          <Button
            variant={isActive ? 'inactive-outline' : 'active-outline'}
            icon={isActive ? CircleMinus : CircleCheck}
            onClick={handleToggle}
            disabled={toggling || saving}
          >
            {toggling ? '…' : isActive ? 'Inactive' : 'Active'}
          </Button>
        )}
        {initial && !isEditing ? (
          <Button
            variant="fill"
            icon={Edit2}
            type="button"
            onClick={() => {
              setIsEditing(true);
              setEditJustEnabled(true);
              if (editGuardTimer.current) clearTimeout(editGuardTimer.current);
              editGuardTimer.current = setTimeout(() => setEditJustEnabled(false), 400);
            }}
          >
            Edit
          </Button>
        ) : (
          <Button
            type="submit"
            form="ob-form"
            variant={initial ? 'update' : 'fill'}
            icon={initial ? Check : Save}
            disabled={saving || toggling || isInvalid || editJustEnabled}
          >
            {initial ? (saving ? 'Updating...' : 'Update') : (saving ? 'Submitting...' : 'Submit')}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

// ── Step builder modal ────────────────────────────────────────────────────────
const StepBuilder: React.FC<{
  obligation: Obligation; authorities: Authority[];
  onClose: () => void;
}> = ({ obligation, authorities, onClose }) => {
  const [steps, setSteps] = useState<ObStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [addingStep, setAddingStep] = useState(false);
  const [newStepName, setNewStepName] = useState('');
  const [newStepDesc, setNewStepDesc] = useState('');
  const [addingAuth, setAddingAuth] = useState<number | null>(null);
  const [newAuthId, setNewAuthId] = useState('');
  const [newAuthRole, setNewAuthRole] = useState('PERFORM');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [reordering, setReordering] = useState(false);

  const loadSteps = useCallback(async () => {
    setLoading(true);
    try { const r = await apiClient.get(`${BASE}/obligations/${obligation.id}/steps`); setSteps(r.data.data ?? []); }
    finally { setLoading(false); }
  }, [obligation.id]);

  useEffect(() => { loadSteps(); }, [loadSteps]);

  const addStep = async () => {
    if (!newStepName.trim()) return;
    await apiClient.post(`${BASE}/obligations/${obligation.id}/steps`, { step_name: newStepName.trim(), description: newStepDesc.trim() || null });
    setNewStepName(''); setNewStepDesc(''); setAddingStep(false);
    await loadSteps();
  };

  const addAuth = async (stepId: number) => {
    if (!newAuthId) return;
    await apiClient.post(`${BASE}/obligation-steps/${stepId}/authorities`, { authority_id: Number(newAuthId), role: newAuthRole });
    setAddingAuth(null); setNewAuthId(''); setNewAuthRole('PERFORM');
    await loadSteps();
  };

  const startEdit = (step: ObStep) => {
    setEditingId(step.id); setEditName(step.step_name); setEditDesc(step.description ?? '');
  };
  const saveEdit = async (stepId: number) => {
    if (!editName.trim()) return;
    await apiClient.put(`${BASE}/obligation-steps/${stepId}`, { step_name: editName.trim(), description: editDesc.trim() || null });
    setEditingId(null);
    await loadSteps();
  };

  const moveStep = async (idx: number, dir: -1 | 1) => {
    const cur = steps[idx], target = steps[idx + dir];
    if (!cur || !target || reordering) return;
    setReordering(true);
    try {
      await apiClient.put(`${BASE}/obligation-steps/${cur.id}`,    { step_number: target.step_number });
      await apiClient.put(`${BASE}/obligation-steps/${target.id}`, { step_number: cur.step_number });
      await loadSteps();
    } finally { setReordering(false); }
  };

  const authOpts = [{ value: '', label: '— Select authority —' }, ...authorities.map(a => ({ value: String(a.id), label: a.name }))];

  return (
    <Modal isOpen onClose={onClose} size="md">
      <Modal.Header>
        Steps — <span className="item-highlight">"{obligation.name}"</span>
      </Modal.Header>
      <Modal.Body className="flex flex-col">
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="collapse-section-stack">
              {[1, 2, 3].map(i => (
                <div key={i} className="collapse-section collapse-section--l1 cs-closed">
                  <div className="collapse-section__header" style={{ pointerEvents: 'none' }}>
                    <span className="collapse-section__toggle"><ChevronDown size={14} /></span>
                    <div className="collapse-section__title-block">
                      <Skeleton.Box height={11} width={60} />
                      <Skeleton.Box height={13} width={180} />
                    </div>
                    <div className="collapse-section__meta">
                      <Skeleton.Box height={18} width={80} radius={999} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : steps.length === 0 ? (
            <NoDataFound title="No steps yet" message='Click "Add Step" below to add the first step.' />
          ) : (
            <div className="collapse-section-stack">
              {steps.map((step, idx) => {
                const isOpen = expanded === step.id;
                const isEditing = editingId === step.id;
                return (
                  <div key={step.id} className={`collapse-section collapse-section--l1 ${isOpen ? 'cs-open' : 'cs-closed'}`}>
                    <div className="collapse-section__header"
                      onClick={() => !isEditing && setExpanded(isOpen ? null : step.id)}
                      style={{ cursor: isEditing ? 'default' : 'pointer' }}>
                      <span className="collapse-section__toggle"><ChevronDown size={14} /></span>
                      {isEditing ? (
                        <div className="collapse-section__title-block" style={{ gap: 6 }} onClick={e => e.stopPropagation()}>
                          <FormControl.Input autoFocus value={editName} onChange={e => setEditName(e.target.value)}
                            onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && saveEdit(step.id)} placeholder="Step name *" />
                          <FormControl.Input value={editDesc} onChange={e => setEditDesc(e.target.value)} placeholder="Description (optional)" />
                        </div>
                      ) : (
                        <div className="collapse-section__title-block">
                          <span className="collapse-section__code">Step {step.step_number}</span>
                          <span className="collapse-section__name">{step.step_name}</span>
                          {step.description && <span className="collapse-section__desc">{step.description}</span>}
                        </div>
                      )}
                      <div className="collapse-section__meta" onClick={e => { if (isEditing) e.stopPropagation(); }}>
                        {isEditing ? (
                          <>
                            <button type="button" onClick={() => saveEdit(step.id)} title="Save"
                              className="text-green-500 hover:text-green-600"><Check size={15} color="var(--interactive-success-bg)" /></button>
                            <button type="button" onClick={() => setEditingId(null)} title="Cancel"
                              className="text-gray-400 hover:text-gray-600"><X size={15} color="var(--text-muted)" /></button>
                          </>
                        ) : (
                          <>
                            <span className="status-badge sm info">{step.authorities.length} {step.authorities.length === 1 ? 'authority' : 'authorities'}</span>
                            <div className="flex flex-col -my-1">
                              <button type="button" disabled={idx === 0 || reordering} title="Move up"
                                onClick={e => { e.stopPropagation(); moveStep(idx, -1); }}
                                className="text-gray-300 hover:text-blue-500 disabled:opacity-30"><ArrowUp size={12} /></button>
                              <button type="button" disabled={idx === steps.length - 1 || reordering} title="Move down"
                                onClick={e => { e.stopPropagation(); moveStep(idx, 1); }}
                                className="text-gray-300 hover:text-blue-500 disabled:opacity-30"><ArrowDown size={12} /></button>
                            </div>
                            <button type="button" onClick={e => { e.stopPropagation(); startEdit(step); }} title="Edit step"
                              className="table-list-action table-list-action--edit"><Edit2 size={13} /></button>
                            <button type="button" title="Delete step" className="btn-danger-outline-none"
                              onClick={e => { e.stopPropagation(); apiClient.delete(`${BASE}/obligation-steps/${step.id}`).then(loadSteps); }}><Trash2 size={13} /></button>
                          </>
                        )}
                      </div>
                    </div>
                    {isOpen && (
                      <div className="collapse-section__body">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1">Authorities</p>
                        {step.authorities.length === 0 ? (
                          <p className="collapse-section__empty">No authorities defined.</p>
                        ) : (
                          step.authorities.map(sa => (
                            <div key={sa.id} className="collapse-card">
                              <div className="collapse-card__row">
                                <span className="collapse-card__title" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                  <Users size={11} className="text-blue-400" />{sa.authority_name}
                                </span>
                                <div className="collapse-card__meta">
                                  <span className="status-badge sm neutral">{ROLE_LABEL[sa.role] ?? sa.role}</span>
                                  <button type="button" title="Remove authority" className="btn-danger-outline-none"
                                    onClick={() => apiClient.delete(`${BASE}/obligation-step-authorities/${sa.id}`).then(loadSteps)}><X size={10} /></button>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                        {addingAuth === step.id ? (
                          <div className="flex items-center gap-2 mt-2">
                            <div className="flex-1 min-w-0">
                              <FormControl.SearchSelect options={authOpts} value={newAuthId}
                                onChange={v => setNewAuthId(v ? String(v) : '')} placeholder="Select authority…" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <FormControl.SearchSelect
                                options={ROLES.map(r => ({ value: r, label: ROLE_LABEL[r] }))}
                                value={newAuthRole}
                                onChange={v => setNewAuthRole(String(v ?? 'PERFORM'))}
                              />
                            </div>
                            <button type="button" title="Add authority" className="btn-icon-only flex-shrink-0"
                              onClick={() => addAuth(step.id)}><Plus size={16} /></button>
                            <button type="button" title="Cancel" className="btn-danger-outline-none flex-shrink-0"
                              onClick={() => setAddingAuth(null)}><X size={14} /></button>
                          </div>
                        ) : (
                          <div className="flex justify-end">
                            <Button variant="fill" icon={Plus} onClick={() => { setAddingAuth(step.id); setNewAuthId(''); setNewAuthRole('PERFORM'); }}>
                              Create Authority
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {addingStep ? (
            <div className="border border-blue-200 rounded-xl p-4 bg-blue-50/30 space-y-2 mt-2">
              <FormControl.Input autoFocus placeholder="Step name *" value={newStepName}
                onChange={e => setNewStepName(e.target.value)}
                onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && addStep()} />
              <FormControl.Textarea rows={2} placeholder="Description (optional)"
                value={newStepDesc} onChange={e => setNewStepDesc(e.target.value)} />
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => { setAddingStep(false); setNewStepName(''); setNewStepDesc(''); }}>Cancel</Button>
                <Button variant="fill" icon={Plus} onClick={addStep}>Create Step</Button>
              </div>
            </div>
          ) : (
            <button type="button" onClick={() => setAddingStep(true)}
              className="w-full flex items-center justify-center gap-2 py-2.5 mt-2 text-sm text-blue-500 border border-dashed border-blue-200 rounded-xl hover:bg-blue-50 transition">
              <Plus size={14} /> Create Step
            </button>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="fill" onClick={onClose}>Done</Button>
      </Modal.Footer>
    </Modal>
  );
};

// ── Criticality badge variant ─────────────────────────────────────────────────
const criticalityVariant = (c: string | null | undefined) => {
  switch (c) {
    case 'Critical': return 'danger';
    case 'High':     return 'warning';
    case 'Medium':   return 'info';
    default:         return 'neutral';
  }
};

// ── Main component ────────────────────────────────────────────────────────────
const ComplianceFilings: React.FC = () => {
  const { check } = usePermissions();
  const { allowed: canView, loading: viewLoading } = usePermission('COM_COMPLIANCE_REGISTER', 'view');
  const canRegister = check('COM_COMPLIANCE_REGISTER', 'view+modify').allowed;

  const [obligations, setObligations] = useState<Obligation[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ACTIVE' | 'INACTIVE'>('ACTIVE');
  const [criticalityFilter, setCriticalityFilter] = useState('');
  const [laws, setLaws] = useState<ApplicableLaw[]>([]);
  const [authorities, setAuthorities] = useState<Authority[]>([]);
  const [locations, setLocations] = useState<LocationLite[]>([]);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [editOb, setEditOb] = useState<Obligation | null>(null);
  const [showObModal, setShowObModal] = useState(false);
  const [stepBuilderFor, setStepBuilderFor] = useState<Obligation | null>(null);
  const [invokeFor, setInvokeFor] = useState<Obligation | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Server-side fetch — all filters + pagination pushed to the backend.
  const loadObligations = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page, limit: pageSize, status: statusFilter };
      if (criticalityFilter) params.criticality = criticalityFilter;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      const r = await apiClient.get(`${BASE}/obligations`, { params });
      setObligations(r.data.data ?? []);
      setTotalRecords(r.data.meta?.total ?? r.data.total ?? 0);
    } catch {
      setToast({ type: 'error', message: 'Failed to load obligations.' });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, statusFilter, criticalityFilter, debouncedSearch]);

  // One-time reference data
  useEffect(() => {
    // Approved + active laws only — getAll({status:'ACTIVE'}) leaked draft/submitted
    // laws into the Act/Law picker.
    applicableLawsService.getDropdown().then(setLaws).catch(() => {});
    // Approved + active authorities only — getAuthorities() leaked draft/unapproved
    // authorities (e.g. "Director 22", "Test") into the pickers. Still needed by the
    // step editor and the invoke modal; the obligation header no longer names one.
    authorityApiService.getAuthoritiesDropdown()
      .then(r => setAuthorities((r.items ?? []) as unknown as Authority[]))
      .catch(() => {});
    sopInstanceService.listEntityLocations().then(setLocations).catch(() => {});
  }, []);

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Re-fetch whenever page/size/filters change.
  useEffect(() => { if (canView) loadObligations(); }, [loadObligations, canView]);

  // Reset to page 1 when any filter (not the page itself) changes.
  useEffect(() => { setPage(1); }, [debouncedSearch, statusFilter, criticalityFilter, pageSize]);

  const handleToggleActive = async (ob: Obligation, next: number) => {
    try {
      await apiClient.put(`${BASE}/obligations/${ob.id}`, { is_active: next });
      setToast({ type: next ? 'success' : 'warning', message: next ? 'Reactivated.' : 'Inactivated.' });
      setShowObModal(false); setEditOb(null);
      loadObligations();
    } catch {
      setToast({ type: 'error', message: 'Failed to update status.' });
    }
  };

  const handleSaved = (_ob: Obligation) => {
    setShowObModal(false); setEditOb(null);
    setToast({ type: 'success', message: editOb ? 'Obligation updated.' : 'Obligation created.' });
    loadObligations();
  };

  const handleInvokeResult = (msg: string) => {
    if (msg.startsWith('error:')) setToast({ type: 'error', message: msg.slice(6) });
    else { setToast({ type: 'success', message: msg }); setInvokeFor(null); }
  };

  const paginatedObs = obligations;

  if (!viewLoading && !canView) {
    return (
      <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
        <NoDataFound title="Not authorised" message="You do not have access to 'COM_COMPLIANCE_REGISTER'." />
      </div>
    );
  }

  return (
    <div className="flex h-full overflow-hidden">
      <Toast toast={toast} onClose={() => setToast(null)} />

      {/* ── Content card (fixed-zone: toolbar pinned, body scrolls) ── */}
      <div className="netbody-card side-content-card--fixed flex flex-col flex-1 min-w-0">

        {/* Toolbar */}
        <div className="netbody-header">
          {/* Status — iOS-style on/off switch (on = active) */}
          <StatusSwitch
            value={statusFilter === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE'}
            onChange={next => setStatusFilter(next)}
            noun="filings"
            testId="SEL_ob_status_filter"
          />

          <div className="flex items-center gap-2 ml-auto">
            <FormControl.SearchSelect
              className="filter-status filter-status--wide"
              data-testid="SEL_ob_criticality_filter"
              options={[{ value: '', label: 'All Criticality' }, ...CRITICALITIES.map(c => ({ value: c, label: c }))]}
              value={criticalityFilter}
              onChange={v => setCriticalityFilter(v ? String(v) : '')}
              placeholder="All Criticality"
            />

            <button
              onClick={loadObligations}
              className="p-1.5 text-gray-400 hover:text-blue-500 transition"
              title="Refresh"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </button>

            <FormControl.Input
              type="text"
              placeholder="Search obligations…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              leadingIcon={<Search size={14} />}
              className="filter-search"
            />

            {canRegister && (
              <Button
                variant="fill"
                icon={Plus}
                onClick={() => { setEditOb(null); setShowObModal(true); }}
              >
                Create
              </Button>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="netbody-content">
          <div className="scroll-container">
            {loading ? (
              <div className="action-card-grid action-card-grid--single">
                <CardSkeleton count={6} />
              </div>
            ) : obligations.length === 0 ? (
              <NoDataFound
                title={statusFilter === 'INACTIVE' ? 'No Inactive Obligations' : 'No Obligations Found'}
                message={search || statusFilter !== 'ACTIVE' || criticalityFilter
                  ? 'No obligations match your filter.'
                  : 'Click "Create" to add the first one.'}
              />
            ) : (
              <div className="action-card-grid action-card-grid--single">
                {paginatedObs.map(ob => (
                  <div
                    key={ob.id}
                    className="action-card"
                    role="button"
                    tabIndex={0}
                    style={(ob.is_active ?? 1) === 0 ? { opacity: 0.6 } : undefined}
                    onClick={() => { if (canRegister) { setEditOb(ob); setShowObModal(true); } }}
                    onKeyDown={e => { if ((e.key === 'Enter' || e.key === ' ') && canRegister) { e.preventDefault(); setEditOb(ob); setShowObModal(true); } }}
                  >
                    {/* Header */}
                    <div className="action-card__header">
                      <div className="action-card__icon">
                        <FileText size={16} />
                      </div>
                      <div className="action-card__title-block">
                        <span className="action-card__title">{ob.name}</span>
                        {ob.obligation_type && (
                          <span className="action-card__subtitle">{ob.obligation_type}</span>
                        )}
                      </div>
                      <div className="action-card__badge" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <span data-testid={`TXT_obligations_row_${ob.id}_code`}>
                          {ob.code
                            ? <Badge variant="neutral" size="sm">{ob.code}</Badge>
                            : <span style={{ color: '#9ca3af' }}>—</span>}
                        </span>
                        {(ob.is_active ?? 1) === 0 && (
                          <Badge variant="neutral" size="sm">Inactive</Badge>
                        )}
                        <Badge variant={statusVariant(ob.ob_status ?? 'DRAFT')} size="sm">
                          {ob.ob_status ?? 'DRAFT'}
                        </Badge>
                        {canRegister && (
                          <>
                            <Button
                              variant="secondary-outline"
                              icon={Settings}
                              onClick={e => { e.stopPropagation(); setStepBuilderFor(ob); }}
                              onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); setStepBuilderFor(ob); } }}
                              title="Define Steps"
                            >
                              Steps
                            </Button>
                            {(() => {
                              const noSteps = (ob.step_count ?? 0) === 0;
                              const doRollout = (e: React.SyntheticEvent) => {
                                e.stopPropagation();
                                if (noSteps) {
                                  setToast({ type: 'error', message: 'Obligation has no steps. Define steps before rollout.' });
                                  return;
                                }
                                setInvokeFor(ob);
                              };
                              return (
                            <Button
                              variant="secondary-outline"
                              icon={Rocket}
                              style={noSteps ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                              onClick={doRollout}
                              onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') doRollout(e); }}
                              title={noSteps ? 'Define steps before rollout' : 'Rollout'}
                            >
                              Rollout
                            </Button>
                              );
                            })()}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Meta row */}
                    <div className="action-card__meta action-card__meta--piped">
                      {ob.law_name && (
                        <span className="action-card__meta-item">
                          <BookOpen size={11} />{ob.law_name}
                        </span>
                      )}
                      <span className="action-card__meta-item">
                        {ob.step_count ?? 0} step{(ob.step_count ?? 0) !== 1 ? 's' : ''}
                      </span>
                      {ob.criticality && (
                        <Badge variant={criticalityVariant(ob.criticality)} size="sm">
                          {ob.criticality}
                        </Badge>
                      )}
                    </div>

                    {ob.provision_description && (
                      <p className="text-[13px] text-gray-500 mt-1 line-clamp-1">
                        {ob.provision_description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            <Pagination
              page={page}
              pageSize={pageSize}
              totalRecords={totalRecords}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              pageSizeOptions={[10, 25, 50, 100]}
              recordLabel="obligations"
            />
          </div>
        </div>
      </div>

      {/* ── Modals ──────────────────────────────────────────────────── */}
      {showObModal && (
        <ObligationModal
          initial={editOb}
          laws={laws}
          onClose={() => { setShowObModal(false); setEditOb(null); }}
          onSaved={handleSaved}
          onToggleActive={editOb ? handleToggleActive : undefined}
        />
      )}

      {stepBuilderFor && (
        <StepBuilder
          obligation={stepBuilderFor}
          authorities={authorities}
          onClose={async () => { await loadObligations(); setStepBuilderFor(null); }}
        />
      )}

      {invokeFor && (
        <InvokeModal
          obligation={invokeFor}
          locations={locations}
          authorities={authorities}
          onClose={() => setInvokeFor(null)}
          onSuccess={handleInvokeResult}
        />
      )}
    </div>
  );
};

export default ComplianceFilings;
